import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Phone, MapPin, AlertTriangle, Pill, Heart, Shield, Building2,
  Wifi, WifiOff, Download, RefreshCw, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'fendanacura_emergency_info';

export default function OfflineEmergencyInfo() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchData = async () => {
    try {
      const { data: info } = await axios.get(`${API_URL}/api/caregiver-management/emergency-info`, { withCredentials: true });
      setData(info);
      localStorage.setItem(CACHE_KEY, JSON.stringify(info));
      toast.success('Emergency info synced');
    } catch (err) {
      // Try loading from cache
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        setData(JSON.parse(cached));
        toast.info('Loaded from offline cache');
      } else {
        toast.error('No data available');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="offline-emergency-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Emergency Info</h1>
            <p className="text-sm text-muted-foreground mt-1">Available offline for emergencies</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${
              isOnline ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-red-400/10 text-red-400'
            }`} data-testid="online-status">
              {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {isOnline ? 'Online' : 'Offline'}
            </span>
            <Button variant="outline" size="sm" onClick={fetchData}
              className="border-border text-foreground hover:bg-muted gap-2" data-testid="sync-btn">
              <RefreshCw className="w-4 h-4" /> Sync
            </Button>
          </div>
        </div>

        {data?.last_updated && (
          <p className="text-xs text-muted-foreground">
            Last synced: {new Date(data.last_updated).toLocaleString()}
          </p>
        )}

        {/* Emergency Numbers */}
        <Card className="glass-card border-red-500/30">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-red-400 flex items-center gap-2">
              <Phone className="w-5 h-5" /> Emergency Numbers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-3 gap-3">
              {data?.emergency_numbers && Object.entries(data.emergency_numbers).map(([key, value]) => (
                <div key={key} className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                  <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                  <p className="text-foreground font-bold text-lg mt-1">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Organization Contact */}
        {data?.organization && Object.keys(data.organization).length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#00A6A0]" /> Agency Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {data.organization.name && (
                  <p className="text-foreground font-medium">{data.organization.name}</p>
                )}
                {data.organization.phone && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#00A6A0]" />{data.organization.phone}
                  </p>
                )}
                {data.organization.email && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4 text-[#00A6A0]" />{data.organization.email}
                  </p>
                )}
                {data.organization.address && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#00A6A0]" />{data.organization.address}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Emergency Contacts */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <User className="w-5 h-5 text-[#FF6F61]" /> My Emergency Contacts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(!data?.personal_contacts || data.personal_contacts.length === 0) ? (
              <div className="p-6 text-center">
                <p className="text-muted-foreground text-sm">No emergency contacts added yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.personal_contacts.map((c, i) => (
                  <div key={i} className="p-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#FF6F61]/10">
                      <Phone className="w-4 h-4 text-[#FF6F61]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{c.name || 'Contact'}</p>
                      <p className="text-xs text-muted-foreground">{c.phone || c.number || ''} &bull; {c.relationship || ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Client Medical Info */}
        {data?.client_medical_info && data.client_medical_info.length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <Heart className="w-5 h-5 text-rose-400" /> Today's Clients - Key Medical Info
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {data.client_medical_info.map((client, i) => (
                  <div key={i} className="p-4 space-y-3" data-testid={`client-info-${i}`}>
                    <div className="flex items-center gap-2">
                      <p className="text-foreground font-medium">{client.client_name}</p>
                      {client.address && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{client.address}
                        </span>
                      )}
                    </div>
                    {client.allergies && client.allergies.length > 0 && (
                      <div>
                        <p className="text-xs text-red-400 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Allergies
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {client.allergies.map((a, j) => (
                            <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-red-400/10 text-red-400">
                              {typeof a === 'string' ? a : a.name || ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {client.medications && client.medications.length > 0 && (
                      <div>
                        <p className="text-xs text-blue-400 font-medium flex items-center gap-1">
                          <Pill className="w-3 h-3" /> Medications
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {client.medications.map((m, j) => (
                            <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-blue-400/10 text-blue-400">
                              {typeof m === 'string' ? m : m.name || ''}
                              {m.dosage ? ` (${m.dosage})` : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {client.conditions && client.conditions.length > 0 && (
                      <div>
                        <p className="text-xs text-amber-400 font-medium flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Conditions
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {client.conditions.map((c, j) => (
                            <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400">
                              {typeof c === 'string' ? c : c.name || ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {client.emergency_notes && (
                      <div className="p-2 rounded-lg bg-red-500/5 border border-red-500/20">
                        <p className="text-xs text-red-400">{client.emergency_notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Protocols */}
        {data?.protocols && data.protocols.length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#00A6A0]" /> Emergency Protocols
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {data.protocols.map((p, i) => (
                  <div key={p.id || i} className="p-4">
                    <p className="text-sm font-medium text-foreground">{p.title || p.name || 'Protocol'}</p>
                    <p className="text-xs text-muted-foreground mt-1">{p.description || p.content || ''}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
